import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@greencandle/api-client'
import { api } from '../api'

/**
 * AST-1. Registers a property or a private loan as a CUSTOM instrument - a name, a code and a
 * currency, nothing else. Once it exists it shows up in the ordinary transaction form's
 * instrument picker like any other instrument, so buying it (opening its lot) and later
 * updating its value both reuse machinery that already exists rather than this form growing to
 * cover them too.
 */
export function CustomAssetForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [currency, setCurrency] = useState('USD')

  const create = useMutation({
    mutationFn: () =>
      api.createInstrument({ assetClass: 'CUSTOM', symbol: code, name, currency }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['instruments'] })
      onDone()
    },
  })

  function submit(event: FormEvent) {
    event.preventDefault()
    create.mutate()
  }

  return (
    <form className="card" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h3 className="card-title">New custom asset</h3>
      <p className="muted" style={{ marginTop: 0 }}>
        A property or a private loan - anything nothing prices automatically. Buy it with an
        ordinary transaction afterward, the same way you would a stock.
      </p>

      <div className="entry-grid">
        <label>
          Name
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="123 Main St apartment"
            required
          />
        </label>
        <label>
          Code
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="MAIN-ST-APT"
            required
          />
        </label>
        <label>
          Currency
          <input
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
            maxLength={3}
            required
          />
        </label>
      </div>

      {create.isError && (
        <div className="notice error" role="alert" style={{ marginTop: 8 }}>
          {create.error instanceof ApiError
            ? (create.error.problem.detail ?? create.error.message)
            : 'Could not reach the API.'}
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <button type="submit" className="primary" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create'}
        </button>{' '}
        <button type="button" onClick={onDone}>Cancel</button>
      </div>
    </form>
  )
}
